import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, User, Bot, Trash2 } from "lucide-react";
import { askAssistant } from "@/lib/ai-assistant.functions";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/assistant")({
  component: AssistantPage,
  head: () => ({ meta: [{ title: "KI-Assistent — InboxPulse" }] }),
});

type Msg = { role: "user" | "assistant"; content: string };

const STARTER_PROMPTS = [
  "Wie starte ich meine erste Kampagne Schritt für Schritt?",
  "Was ist der Unterschied zwischen Bounce, Complaint und Spam?",
  "Wie verbessere ich meine Zustellbarkeit bei Gmail?",
  "Erklär mir VERP einfach.",
  "Wo trage ich meinen SMTP-Server ein?",
  "Was bedeutet eine Open-Rate von 15 %?",
];

function AssistantPage() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const ask = useServerFn(askAssistant);

  const mutation = useMutation({
    mutationFn: async (history: Msg[]) => ask({ data: { messages: history } }),
    onSuccess: (res) => {
      setMessages((prev) => [...prev, { role: "assistant", content: res.reply }]);
    },
    onError: (e: Error) => toast.error(e.message || "KI-Anfrage fehlgeschlagen"),
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, mutation.isPending]);

  function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed || mutation.isPending) return;
    const next: Msg[] = [...messages, { role: "user", content: trimmed }];
    setMessages(next);
    setInput("");
    mutation.mutate(next);
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <PageHeader
        title="KI-Assistent"
        description="Frag alles über InboxPulse. Der Assistent kennt deine App und kann dir bei jedem Schritt helfen."
      />

      <HelpPanel
        title="Hilfe: Wenn du nicht weiterweißt — frag die KI"
        what={
          <>
            Ein <b>Chat mit einer KI</b>, die genau diese App kennt. Du kannst auf Deutsch fragen,
            was du willst — sie erklärt jeden Schritt.
          </>
        }
        why={
          <>
            Schneller als Doku lesen. Beispiele: "Wie lege ich eine neue Kampagne an?", "Warum
            bouncen meine Mails an web.de?", "Was bedeutet DKIM-Fehler?"
          </>
        }
        how={[
          <>Frage unten ins Feld tippen.</>,
          <>Senden — Antwort kommt nach 1-3 Sekunden.</>,
          <>Nachfragen sind erlaubt — die KI merkt sich den Gesprächs-Verlauf.</>,
        ]}
        tips={[
          <>
            Je konkreter die Frage, desto besser die Antwort. "Mein Bounce-Rate ist 8%, was tun?"
            ist besser als "Hilfe!".
          </>,
          <>Bei sensiblen Daten (echte Passwörter, Adressen) nicht in den Chat schreiben.</>,
        ]}
      />

      <Card className="bg-card/60 border-border flex flex-col h-[calc(100vh-220px)]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div>
              <div className="text-center mb-6">
                <div className="size-12 mx-auto rounded-full bg-[image:var(--gradient-primary)] grid place-items-center shadow-[var(--shadow-glow)] mb-3">
                  <Sparkles className="size-6 text-primary-foreground" />
                </div>
                <h3 className="font-semibold">Wie kann ich helfen?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Wähle eine Beispiel-Frage oder schreib deine eigene.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {STARTER_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => submit(p)}
                    className="text-left text-sm px-4 py-3 rounded-lg border border-border bg-muted/30 hover:bg-muted/60 hover:border-primary/40 transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={cn("flex gap-3", m.role === "user" && "flex-row-reverse")}>
              <div
                className={cn(
                  "size-8 shrink-0 rounded-full grid place-items-center",
                  m.role === "user" ? "bg-primary/20" : "bg-muted",
                )}
              >
                {m.role === "user" ? <User className="size-4" /> : <Bot className="size-4" />}
              </div>
              <div
                className={cn(
                  "rounded-lg px-4 py-2.5 text-sm max-w-[80%] whitespace-pre-wrap leading-relaxed",
                  m.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground",
                )}
              >
                {m.content}
              </div>
            </div>
          ))}

          {mutation.isPending && (
            <div className="flex gap-3">
              <div className="size-8 shrink-0 rounded-full grid place-items-center bg-muted">
                <Bot className="size-4" />
              </div>
              <div className="rounded-lg px-4 py-2.5 text-sm bg-muted text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" /> Denke nach…
              </div>
            </div>
          )}
        </div>

        <form
          className="border-t border-border p-3 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
        >
          {messages.length > 0 && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => setMessages([])}
              title="Chat zurücksetzen"
            >
              <Trash2 className="size-4" />
            </Button>
          )}
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Frag den Assistenten… (Enter = senden, Shift+Enter = neue Zeile)"
            rows={2}
            className="resize-none text-sm flex-1"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
          />
          <Button type="submit" disabled={mutation.isPending || !input.trim()} className="gap-1.5">
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <Send className="size-4" /> Senden
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
